// src/App.js — DMS merged portal (dms.jivini.in) — Phase 1
//
// Single login, single portal, 14-tab structure per commercialization spec.
// Routes are registered under BOTH the new Sidebar nav paths AND the
// legacy internal paths some warehouse-sourced pages still link to
// internally (their own WORKFLOW_LINKS cross-nav) — both point at the
// same components, so nothing 404s regardless of which link was clicked.
//
// NOTE (known cosmetic issue, not yet fixed): AssignDeliveries, QueuePage
// (used by Warehouse subtabs), StageOverride, and Vouchers each render
// their own <Header/> component (warehouse-portal's original standalone
// nav bar) in addition to this app's single <Sidebar/>. Functionally
// everything works; visually you'll see two nav bars stacked on those
// pages until a follow-up cleanup pass removes the inner <Header/> calls.
import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import Sidebar from './components/Sidebar';
import { ThemeProvider } from './context/ThemeContext';

import Login             from './pages/Login';
import Dashboard         from './pages/Dashboard';
import Heatmap           from './pages/Heatmap';
import InvoiceDetail     from './pages/InvoiceDetail';
import DispatchOverview  from './pages/DispatchOverview';
import LiveMap           from './pages/LiveMap';
import Teams             from './pages/Teams';
import Transports        from './pages/Transports';
import Reports           from './pages/Reports';
import Settings          from './pages/Settings';
import PlatformAdmin      from './pages/PlatformAdmin';
import Exceptions        from './pages/Exceptions';
import Messages          from './pages/Messages';

import StockRemoval      from './pages/StockRemoval';
import Checking          from './pages/Checking';
import AssignDeliveries  from './pages/AssignDeliveries';
import Vouchers          from './pages/Vouchers';
import DayAccounting from './pages/DayAccounting';
import LocationApprovals from './pages/LocationApprovals';
import ReturnLabels from './pages/ReturnLabels';
import InwardRegister from './pages/InwardRegister';

const ALLOWED_ROLES = ['owner', 'team_leader', 'stock_removal', 'checking', 'booking', 'billing', 'delivery_person'];

// NOTE (Phase 1 simplification, intentional): this checks only that a
// valid session exists — it does NOT yet restrict which of the 14 tabs
// each role can reach. Per-tab access control is exactly what item 6.1
// (checkbox-based per-user permissions) is meant to solve, and needs a
// users table schema migration first. Until that's built, ANY logged-in
// user (owner, team_leader, or any warehouse role) can navigate to any
// tab via the URL — the Sidebar doesn't hide tabs by role either. Do not
// treat this as real access control yet.
function ProtectedRoute({ children }) {
  const token = localStorage.getItem('dms_token');
  if (!token) return <Navigate to="/" replace />;

  let user = null;
  try {
    user = JSON.parse(localStorage.getItem('dms_user') || 'null');
  } catch {
    user = null;
  }
  if (!user || !ALLOWED_ROLES.includes(user.role)) {
    localStorage.removeItem('dms_token');
    localStorage.removeItem('dms_user');
    return <Navigate to="/" replace />;
  }

  return (
    <div className="flex flex-col h-screen bg-gray-100 dark:bg-gray-900">
      <Sidebar />
      <main className="flex-1 overflow-auto dark:text-gray-100">{children}</main>
    </div>
  );
}

// Simple 2-tab landing page for the "Warehouse" nav item (Stock-Removal /
// Checking subtabs). Packing was removed as a separate manual step —
// Checking now auto-advances straight through to "Ready for Delivery"
// on the backend, so there's nothing left for a human to do at a
// separate Packing stage anymore.
function Warehouse() {
  const wUser = (() => { try { return JSON.parse(localStorage.getItem('dms_user') || '{}'); } catch (e) { return {}; } })();
  const wPerms = wUser.permissions || {};
  const wAdmin = ['owner', 'team_leader'].includes(wUser.role);
  // Sessions opened before the permission split still carry warehouse_ops:
  // treat them as before until that user logs in again.
  const wStale = wPerms.warehouse_ops === true && wPerms.stock_removal === undefined && wPerms.checking === undefined;
  const ALL_TABS = [
    { key: 'stock-removal', label: 'Stock Removal', Component: StockRemoval, ok: wAdmin || wStale || wPerms.stock_removal === true },
    { key: 'checking',      label: 'Checking',       Component: Checking,      ok: wAdmin || wStale || wPerms.checking === true },
  ];
  const TABS = ALL_TABS.filter(t => t.ok);
  const [tab, setTab] = React.useState((TABS[0] || ALL_TABS[0]).key);
  const Active = (TABS.find(t => t.key === tab) || TABS[0] || ALL_TABS[0]).Component;
  return (
    <div style={{ "--sticky-offset": "53px" }}>
      <div className="flex gap-1 px-6 items-center bg-white border-b border-gray-200 sticky top-0 z-20" style={{ height: "53px", boxSizing: "border-box" }}>
        {TABS.map(t => (
          <button
            key={t.key}
            onClick={() => setTab(t.key)}
            className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${
              tab === t.key ? 'bg-brand-700 text-white' : 'text-gray-600 hover:bg-gray-100'
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>
      <Active />
    </div>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <BrowserRouter>
        <Routes>
          <Route path="/" element={localStorage.getItem('dms_token') ? <Navigate to="/dashboard" replace /> : <Login />} />

          {/* --- Sidebar's 14-tab paths --- */}
          <Route path="/dashboard"       element={<ProtectedRoute><Dashboard /></ProtectedRoute>} />
          <Route path="/heatmap"         element={<ProtectedRoute><Heatmap /></ProtectedRoute>} />
          <Route path="/invoices" element={<Navigate to="/dashboard" replace />} />
          <Route path="/invoices/:id"    element={<ProtectedRoute><InvoiceDetail /></ProtectedRoute>} />
          <Route path="/dispatch"        element={<ProtectedRoute><DispatchOverview /></ProtectedRoute>} />
          <Route path="/live-map"        element={<ProtectedRoute><LiveMap /></ProtectedRoute>} />
          <Route path="/teams"           element={<ProtectedRoute><Teams /></ProtectedRoute>} />
          <Route path="/transports"      element={<ProtectedRoute><Transports /></ProtectedRoute>} />
          <Route path="/reports"         element={<ProtectedRoute><Reports /></ProtectedRoute>} />
          <Route path="/settings"        element={<ProtectedRoute><Settings /></ProtectedRoute>} />
          <Route path="/platform-admin" element={<ProtectedRoute><PlatformAdmin /></ProtectedRoute>} />
          <Route path="/messages"        element={<ProtectedRoute><Messages /></ProtectedRoute>} />
          <Route path="/warehouse"       element={<ProtectedRoute><Warehouse /></ProtectedRoute>} />
          <Route path="/assign"          element={<ProtectedRoute><AssignDeliveries /></ProtectedRoute>} />
          <Route path="/vouchers"        element={<ProtectedRoute><Vouchers /></ProtectedRoute>} />
          <Route path="/day-accounting" element={<ProtectedRoute><DayAccounting /></ProtectedRoute>} />
          <Route path="/location-approvals" element={<ProtectedRoute><LocationApprovals /></ProtectedRoute>} />
          <Route path="/return-labels" element={<ProtectedRoute><ReturnLabels /></ProtectedRoute>} />
          <Route path="/inward" element={<ProtectedRoute><InwardRegister /></ProtectedRoute>} />

          {/* --- Legacy internal paths (still referenced by WORKFLOW_LINKS
               inside AssignDeliveries/QueuePage/StageOverride/Vouchers) ---
               Point at the same components so old in-app links don't 404. */}
          <Route path="/stock-removal"   element={<ProtectedRoute><StockRemoval /></ProtectedRoute>} />
          <Route path="/checking"        element={<ProtectedRoute><Checking /></ProtectedRoute>} />

          {/* Kept from admin-portal, not one of the 14 tabs but still a
              useful drill-down page — reachable via Dashboard's exception
              widgets, not linked in Sidebar. */}
          <Route path="/exceptions"      element={<ProtectedRoute><Exceptions /></ProtectedRoute>} />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
    </ThemeProvider>
  );
}
